/**
 * iOS-only body scroll lock for modals.
 *
 * react-modal toggles `ReactModal__Body--open` on <body>, and our CSS sets
 * `overflow-y: hidden` on that class. Android honours it, but **iOS Safari
 * ignores `overflow: hidden` on <body>** — so the page behind the modal still
 * scrolls. The visible symptom: open a nested popup (`.upper-modal`) and focus
 * an input; iOS raises the keyboard, the modal (`max-height: 98dvh`) is measured
 * top-to-keyboard, and the page behind starts showing through and scrolling.
 *
 * The reliable iOS fix is to pin <body> with `position: fixed` (offset by the
 * current scroll) while any modal is open, then restore the scroll position when
 * the last modal closes. On top of that we attach a non-passive `touchmove`
 * guard that hard-cancels every scroll gesture EXCEPT those inside a genuine
 * inner scroller of the modal / banner (see isScrollAllowed) — so all scrolling
 * from outside the modal is fully disabled while the inner content still scrolls.
 * This is gated to iOS so the already-correct Android / desktop behaviour (plain
 * CSS overflow:hidden) is left untouched.
 *
 * Driven entirely off the presence of the `ReactModal__Body--open` class, which
 * react-modal adds on the first open and removes only when the last modal
 * closes — so nested booking modals are handled correctly.
 */

import {isIOS} from './platform.js';

const MODAL_BODY_CLASS = 'ReactModal__Body--open';

// Roots that "own" a legitimate scroll area: an open modal overlay, the inline
// banner full-screen form, or the Google Places suggestion dropdown. `.upper-modal`
// covers nested popups portalled to <body> (outside the overlay), e.g. the flight
// date/type picker in smartPickupRecommendation — its time list couldn't scroll.
const SCROLL_ROOT_SELECTOR = '.hopn-modal-overlay, .is-banner-fullscreen, .upper-modal';

// Reference-counted so multiple independent lockers (react-modal observer below,
// the inline banner full-screen form, etc.) compose without fighting each other.
let lockCount = 0;
let savedScrollY = 0;

/**
 * The "check": for a given touch target, decide whether the gesture should be
 * allowed to scroll. We allow it only when the touch is inside a genuinely
 * scrollable element that lives within an allowed root — every other gesture
 * (page body, the dimmed overlay background, non-scrollable inner areas) is
 * cancelled, hard-disabling all scrolling outside the modal's inner scroller(s).
 */
function isScrollAllowed(target) {
    if (!(target instanceof Element)) return false;
    // The location-suggestion dropdown lives on <body> (outside the overlay) but must
    // stay scrollable when it overflows. `.hopn-place-suggest` is our own list;
    // `.pac-container` is Google's widget, kept for any input still using it.
    if (target.closest('.hopn-place-suggest') || target.closest('.pac-container')) return true;

    const root = target.closest(SCROLL_ROOT_SELECTOR);
    if (!root) return false; // touch is outside any modal/banner → block

    // Walk up to (and including) the root looking for a real scroll container.
    // The overlay element itself is excluded: dragging the dim background must
    // not scroll it. The banner full-screen root may itself be the scroller.
    let node = target;
    while (node && node !== document.body) {
        if (node.nodeType === 1 && !node.classList.contains('hopn-modal-overlay')) {
            const oy = window.getComputedStyle(node).overflowY;
            if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
                return true;
            }
        }
        if (node === root) break;
        node = node.parentNode;
    }
    return false;
}

function onTouchMove(e) {
    if (!e.cancelable) return;
    // Never block multi-touch (pinch-zoom / accessibility gestures).
    if (e.touches && e.touches.length > 1) return;
    if (!isScrollAllowed(e.target)) {
        e.preventDefault();
    }
}

/**
 * Pin <body> so the page behind can't scroll on iOS, AND hard-cancel any scroll
 * gesture that isn't inside an allowed inner scroller. Balanced by
 * unlockBodyScroll. No-op off iOS (Android/desktop use plain CSS overflow:hidden).
 */
export function lockBodyScroll() {
    if (typeof document === 'undefined' || !isIOS()) return;
    lockCount += 1;
    if (lockCount > 1) return;
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    const {style} = document.body;
    style.position = 'fixed';
    style.top = `-${savedScrollY}px`;
    style.left = '0';
    style.right = '0';
    style.width = '100%';
    // passive:false so we can preventDefault() the disallowed gestures.
    document.addEventListener('touchmove', onTouchMove, {passive: false});
}

export function unlockBodyScroll() {
    if (typeof document === 'undefined' || !isIOS()) return;
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount > 0) return;
    document.removeEventListener('touchmove', onTouchMove, {passive: false});
    const {style} = document.body;
    style.position = '';
    style.top = '';
    style.left = '';
    style.right = '';
    style.width = '';
    // Restore the scroll position the page had before locking.
    window.scrollTo(0, savedScrollY);
}

/**
 * Install the lock once. Safe to call multiple times (no-ops after the first).
 */
export function installIOSModalScrollLock() {
    if (typeof document === 'undefined' || !isIOS()) return;
    if (document.body.dataset.iosScrollLockInstalled === '1') return;
    document.body.dataset.iosScrollLockInstalled = '1';

    let modalLocked = false;
    const sync = () => {
        const open = document.body.classList.contains(MODAL_BODY_CLASS);
        if (open && !modalLocked) {
            modalLocked = true;
            lockBodyScroll();
        } else if (!open && modalLocked) {
            modalLocked = false;
            unlockBodyScroll();
        }
    };

    const observer = new MutationObserver(sync);
    observer.observe(document.body, {attributes: true, attributeFilter: ['class']});
    sync();
}

export default installIOSModalScrollLock;
